import { useRef } from 'react';
import { BookOpen, Calendar, Camera, Sparkles, Trash2, Users } from 'lucide-react';
import { SecureImage } from '@/components/ui/SecureImage';
import { getNameInitials } from '@/lib/utils';

export interface TeacherProfileHeroCardProps {
  name: string;
  /** "Mathematics Teacher" — the designation, or the role when none is recorded. */
  title: string;
  isActive: boolean;
  /** Distinct sections taught. */
  classes: number;
  students: number;
  subjects: number;
  /** "3 years" since joining, or null when no joining date is recorded. */
  tenure: string | null;
  /** The photograph: where it streams from, and a key that changes after an upload. */
  photo: { hasPhoto: boolean; path: string; reloadKey: number };
  isPhotoBusy: boolean;
  onPickPhoto: (file: File) => void;
  onRemovePhoto: () => void;
}

/**
 * The banner at the top of My Profile — who they are and what they teach.
 *
 * Every figure is the teacher's own: the counts come from their allotments and
 * the time at school from their joining date. A figure with nothing behind it
 * shows a dash rather than a sample number.
 */
export function TeacherProfileHeroCard({
  name,
  title,
  isActive,
  classes,
  students,
  subjects,
  tenure,
  photo,
  isPhotoBusy,
  onPickPhoto,
  onRemovePhoto,
}: TeacherProfileHeroCardProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const initials = (
    <span className="grid h-full w-full place-items-center">{getNameInitials(name)}</span>
  );

  return (
    <div className="relative overflow-hidden rounded-3xl border border-blue-100 dark:border-blue-900/40 bg-[#eff6ff] dark:bg-[#172554]/40 p-4  shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
      {/* Ambient background lights and sheen */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 rounded-full bg-gradient-to-br from-blue-400/30 to-indigo-500/20 blur-3xl animate-orb"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-28 right-1/4 h-56 w-56 rounded-full bg-gradient-to-br from-sky-300/25 to-blue-400/10 blur-3xl animate-orb-slow"
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen dark:via-white/10" />
      </div>

      <div className="relative z-10 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-5">
        {/* Left Profile Details */}
        <div className="flex items-center gap-4 min-w-0">
          {/* Avatar with gradient & active status */}
          <div className="relative shrink-0">
            <span className="relative grid h-20 w-20 sm:h-24 sm:w-24 shrink-0 place-items-center overflow-hidden rounded-2xl border border-blue-200/70 dark:border-blue-800/50 bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-2xl sm:text-3xl font-black text-white shadow-lg shadow-blue-500/30">
              {photo.hasPhoto ? (
                <SecureImage
                  path={photo.path}
                  alt={name}
                  reloadKey={photo.reloadKey}
                  className="h-full w-full object-cover"
                  fallback={initials}
                />
              ) : (
                initials
              )}
            </span>
            {/* Their own photograph — the same checks and storage as the office's. */}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              tabIndex={-1}
              aria-hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onPickPhoto(file);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={isPhotoBusy}
              aria-label={photo.hasPhoto ? 'Change your photograph' : 'Add a photograph'}
              title={photo.hasPhoto ? 'Change photograph' : 'Add photograph'}
              className="absolute -top-1.5 -left-1.5 grid h-8 w-8 place-items-center rounded-full bg-surface text-ink-2 border border-line shadow-sm hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60 cursor-pointer"
            >
              <Camera className="h-4 w-4" aria-hidden />
            </button>
            {isActive && (
              <span
                className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full bg-ok text-white text-xs font-bold shadow-sm ring-2 ring-white dark:ring-slate-900"
                title="Active"
              >
                ✓
              </span>
            )}
          </div>

          {/* Teacher Details */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-sm font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-blue-500" aria-hidden />
              {title}
            </span>
            <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white leading-tight">
              {name}
            </h2>
            {photo.hasPhoto && (
              <button
                type="button"
                onClick={onRemovePhoto}
                disabled={isPhotoBusy}
                className="inline-flex w-fit items-center gap-1 rounded-md text-xs font-bold text-ink-3 hover:text-crit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60 cursor-pointer"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Remove photograph
              </button>
            )}
          </div>
        </div>

        {/* Right 4 Metric KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 w-full xl:w-auto shrink-0">
          {/* Card 1: Classes Handled */}
          <div className="bg-white/85 dark:bg-slate-900/80 backdrop-blur-md border border-emerald-200/80 dark:border-emerald-800/40 rounded-2xl py-4 px-2 flex flex-col items-center justify-center min-w-[120px] text-center shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-1.5">
              <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-2xl sm:text-3xl font-black text-emerald-700 dark:text-emerald-400 leading-tight">
              {classes}
            </span>
            <span className="text-sm font-bold text-slate-600 dark:text-slate-300 mt-1">
              {classes === 1 ? 'Class Handled' : 'Classes Handled'}
            </span>
          </div>

          {/* Card 2: Students */}
          <div className="bg-white/85 dark:bg-slate-900/80 backdrop-blur-md border border-blue-200/80 dark:border-blue-800/40 rounded-2xl p-4 flex flex-col items-center justify-center min-w-[120px] text-center shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center mb-1.5">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-2xl sm:text-3xl font-black text-blue-700 dark:text-blue-400 leading-tight">
              {students}
            </span>
            <span className="text-sm font-bold text-slate-600 dark:text-slate-300 mt-1">
              Students
            </span>
          </div>

          {/* Card 3: Subjects / Activities */}
          <div className="bg-white/85 dark:bg-slate-900/80 backdrop-blur-md border border-amber-200/80 dark:border-amber-800/40 rounded-2xl p-4 flex flex-col items-center justify-center min-w-[120px] text-center shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center mb-1.5">
              <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-2xl sm:text-3xl font-black text-amber-700 dark:text-amber-400 leading-tight">
              {subjects}
            </span>
            <span className="text-sm font-bold text-slate-600 dark:text-slate-300 mt-1">
              {subjects === 1 ? 'Subject' : 'Subjects'}
            </span>
          </div>

          {/* Card 4: At School */}
          <div className="bg-white/85 dark:bg-slate-900/80 backdrop-blur-md border border-sky-200/80 dark:border-sky-800/40 rounded-2xl p-4 flex flex-col items-center justify-center min-w-[120px] text-center shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center mb-1.5">
              <Calendar className="w-5 h-5 text-sky-600 dark:text-sky-400" />
            </div>
            <span className="text-2xl sm:text-3xl font-black text-sky-700 dark:text-sky-400 leading-tight">
              {tenure ?? '—'}
            </span>
            <span className="text-sm font-bold text-slate-600 dark:text-slate-300 mt-1">
              At School
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
